import { trackEvent, sanitizeUrlForAnalytics } from "./umami";

const FLAG = "__testsphereFetchTrackingInstalled";

function resolveFetchUrl(input) {
  if (typeof input === "string") return input;
  if (typeof Request !== "undefined" && input instanceof Request)
    return input.url;
  return String(input);
}

function resolveFetchMethod(input, init) {
  if (init && init.method) return String(init.method).toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return (input.method || "GET").toUpperCase();
  }
  return "GET";
}

/**
 * Do not instrument Umami (or other known analytics collector) fetches —
 * otherwise every beacon creates another api.call → cascade of /api/send traffic.
 */
function shouldSkipFetchTracking(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return false;
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const u = new URL(rawUrl, base);
    const h = u.hostname.toLowerCase();
    if (h === "api-gateway.umami.dev") return true;
    if (h === "cloud.umami.is" || h === "umami.is") return true;
    if (h.endsWith(".umami.is") || h.endsWith(".umami.dev")) return true;
  } catch {
    return false;
  }
  return false;
}

/**
 * Wraps window.fetch once; delegates to native fetch (no recursion).
 * Tracks api.call; api.error on network throw or non-OK HTTP status.
 */
export function installFetchAnalytics() {
  if (typeof window === "undefined" || window[FLAG]) return;
  window[FLAG] = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = function trackedFetch(input, init) {
    const rawUrl = resolveFetchUrl(input);
    if (shouldSkipFetchTracking(rawUrl)) {
      return nativeFetch(input, init);
    }
    const url = sanitizeUrlForAnalytics(rawUrl);
    const method = resolveFetchMethod(input, init);
    const start =
      typeof performance !== "undefined" ? performance.now() : Date.now();

    return nativeFetch(input, init).then(
      (response) => {
        const end =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const ms = Math.round(end - start);
        trackEvent("api.call", {
          url,
          method,
          status: response.status,
          response_time_ms: ms,
        });
        if (!response.ok) {
          trackEvent("api.error", {
            url,
            method,
            status: response.status,
            response_time_ms: ms,
          });
        }
        return response;
      },
      (err) => {
        const end =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const ms = Math.round(end - start);
        const fields = {
          url,
          method,
          status: null,
          response_time_ms: ms,
        };
        trackEvent("api.call", fields);
        trackEvent("api.error", fields);
        throw err;
      },
    );
  };
}
